import React, { useEffect } from 'react';
import { useNotification } from './NotificationContext';

const Notification = () => {
  const { notification, hideNotification } = useNotification();

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        hideNotification();
      }, 3000); // Notification will disappear after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [notification, hideNotification]);

  if (!notification) return null;

  const { message, type, color } = notification;

  const getStyles = () => {
    // Determine color classes based on custom color or type
    let bgColorClass, accentColorClass, bgColorStyle, borderColorStyle;
    
    if (color) {
      // Custom color provided
      if (color === 'gray') {
        bgColorClass = 'bg-gray-500';
        accentColorClass = 'border-l-gray-500';
      } else if (color === 'red') {
        bgColorClass = 'bg-red-600';
        accentColorClass = 'border-l-red-600';
      } else if (color === 'green') {
        bgColorClass = 'bg-green-600';
        accentColorClass = 'border-l-green-600';
      } else {
        // Assume it's a hex color (event primary color)
        bgColorStyle = { backgroundColor: color };
        borderColorStyle = { borderLeftColor: color };
      }
    } else {
      // Default colors based on type
      switch (type) {
        case 'error':
          bgColorClass = 'bg-red-500';
          accentColorClass = 'border-l-red-500';
          break;
        case 'info':
          bgColorClass = 'bg-blue-500';
          accentColorClass = 'border-l-blue-500';
          break;
        case 'success':
        default:
          bgColorClass = 'bg-blue-500';
          accentColorClass = 'border-l-blue-500';
          break;
      }
    }

    return {
      icon: (
        <div 
          className={`w-10 h-10 ${bgColorClass} rounded-full flex items-center justify-center flex-shrink-0`}
          style={bgColorStyle}
        >
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {type === 'error' ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            )}
          </svg>
        </div>
      ),
      accent: accentColorClass,
      borderStyle: borderColorStyle
    };
  };

  const styles = getStyles();

  return (
    <div
      className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-4 max-w-md bg-white ${styles.accent} border-l-4 rounded-xl shadow-xl p-4 flex items-center justify-between animate-slide-in backdrop-blur-sm`}
      style={{ zIndex: 'var(--z-notification)', ...styles.borderStyle }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {styles.icon}
        <div className="text-sm font-medium text-gray-900 leading-relaxed break-words">
          {message}
        </div>
      </div>
      <button
        onClick={hideNotification}
        className="text-gray-400 hover:text-gray-600 focus:outline-none ml-4 flex-shrink-0 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default Notification;
